// ExecLearningPage.jsx — exec read-only L&D analytics.
import React, { useEffect, useState } from "react";
import MessengerLayout from "../../layout/MessengerLayout";
import { Download, GraduationCap } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON = "#00FF99";
const AMBER = "#EF9F27";
const RED = "#E24B4A";
const BLUE = "#378ADD";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export default function ExecLearningPage({ embedded = false }) {
  const [k, setK] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [gaps, setGaps] = useState([]);

  useEffect(() => {
    fetch(`${API_BASE}/api/learning/analytics/overview`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : null).then(setK);
    fetch(`${API_BASE}/api/learning/compliance/summary`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : null).then(setCompliance);
    fetch(`${API_BASE}/api/learning/skills/gaps`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : []).then(setGaps);
  }, []);

  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ overview: k, compliance, gaps }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ld_report_${new Date().toISOString().slice(0,10)}.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  const inner = (
    <div className={embedded ? "" : "p-6 space-y-5 text-white"}>
      {!embedded && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={iconBox}><GraduationCap size={16} /></div>
          <h1 style={{ fontSize: 18, fontWeight: 500, color: "#fff", margin: 0 }}>
            Learning & Development — Executive
          </h1>
          <div style={{ flex: 1 }} />
          <button onClick={exportJson} style={btnOutline}>
            <Download size={12} /> Export report
          </button>
        </div>
      )}

      {/* KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
        <Stat label="Completion rate"     value={k ? `${k.completion_rate}%` : "—"} color={NEON} />
        <Stat label="Compliance rate"     value={k ? `${k.compliance_rate}%` : "—"} color={AMBER} />
        <Stat label="L&D spend (qtr)"     value="—" color={BLUE} sub="No spend tracked yet" />
        <Stat label="Certs expiring 30d"  value={k?.certs_expiring_30d ?? "—"} color={RED} />
      </div>

      {/* Department compliance */}
      <div style={{ ...card, marginBottom: 16 }}>
        <h3 style={section}>Department compliance</h3>
        {!compliance ? <p style={{ color: "#666" }}>Loading…</p> :
          compliance.by_department.length === 0 ? <p style={{ color: "#666" }}>No data.</p> :
          <div style={{ display: "grid", gap: 6 }}>
            {compliance.by_department.map((d) => (
              <div key={d.department} style={{ display: "grid", gridTemplateColumns: "140px 1fr 80px", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "#ddd" }}>{d.department}</span>
                <div style={{ height: 10, background: "#1a1a1a", borderRadius: 5, overflow: "hidden" }}>
                  <div style={{
                    width: `${d.rate}%`, height: "100%",
                    background: d.rate >= 80 ? NEON : d.rate >= 50 ? AMBER : RED,
                  }} />
                </div>
                <span style={{ fontSize: 11, color: "#888", textAlign: "right" }}>{d.rate}% ({d.compliant}/{d.total})</span>
              </div>
            ))}
          </div>}
      </div>

      {/* Top skill gaps */}
      <div style={{ ...card, marginBottom: 16 }}>
        <h3 style={section}>Top skill gaps (org-wide)</h3>
        {gaps.length === 0 ? <p style={{ color: "#666" }}>No data.</p> :
          <div style={{ display: "grid", gap: 6 }}>
            {gaps.slice(0, 5).map((g) => (
              <div key={g.skill_id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 80px", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "#ddd" }}>{g.skill_name}</span>
                <div style={{ height: 8, background: "#1a1a1a", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{
                    width: `${g.gap_pct}%`, height: "100%",
                    background: g.gap_pct >= 70 ? RED : g.gap_pct >= 40 ? AMBER : NEON,
                  }} />
                </div>
                <span style={{ fontSize: 11, color: "#666", textAlign: "right" }}>{g.gap_pct}% lacking</span>
              </div>
            ))}
          </div>}
      </div>

      <p style={{ fontSize: 10, color: "#444", marginTop: 16 }}>
        Read-only view. Trend chart and L&D spend tracking would require historical aggregation tables
        and a spend-source integration not present in the current schema.
      </p>
    </div>
  );
  return embedded ? inner : <MessengerLayout>{inner}</MessengerLayout>;
}

function Stat({ label, value, color, sub }) {
  return (
    <div style={card}>
      <div style={{ fontSize: 10, color: "#666", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 24, color, fontWeight: 600, marginTop: 4, fontFamily: "ui-monospace, monospace" }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "#444", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

const card = { background: "#0d0d0d", border: "0.5px solid #1c1c1c", borderRadius: 10, padding: 14 };
const section = { fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, margin: "0 0 10px 0" };
const iconBox = {
  width: 36, height: 36, borderRadius: 10,
  background: "rgba(0,255,153,0.1)", color: NEON,
  display: "flex", alignItems: "center", justifyContent: "center",
  border: `0.5px solid ${NEON}33`,
};
const btnOutline = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "6px 12px", fontSize: 11, borderRadius: 8,
  border: `0.5px solid ${NEON}55`, background: "transparent",
  color: NEON, cursor: "pointer",
};
